// Greige QC — the inspector's screen for §7.2.
//
// No greige lot had ever been judged as a lot: POST /jobwork/:id/quality reads job_work_grn,
// which the Deroi challan path never writes, and the verdict it holds is one slot per JOB-WORK
// ORDER. JWO-DEROI-1739-001 alone made four greige lots against that one slot.
//
// Ruled 25 September: per lot member, per stage. This ships BEFORE the draw gate, deliberately —
// a gate that refuses for want of a verdict QA cannot enter is the makesLots trap again, where
// the backend demanded a field the form never showed.
//
// THE SCREEN SHOWS TARGET BESIDE MEASURED AND THE INSPECTOR RULES. It does not compute pass or
// fail. A GSM four outside tolerance may be a pass on a fabric nobody will notice it on, and a
// shade inside tolerance may still be wrong against the swatch; deciding that from the numbers
// would be a machine overruling the person holding the cloth. The out-of-tolerance mark below is
// a prompt to look, not a verdict.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { askReason } from '../utils/reportError';
const RELEASERS = ['admin', 'management'];   // mirrors authorize() on POST /lot-members/:id/qc/release

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
const inputStyle = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box', width: '100%' };
const labelStyle = { fontSize: 10, color: '#888', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase' };
const VERDICT = {
  pass:        { bg: '#EAF7EE', c: '#166534', label: '✅ Pass' },
  conditional: { bg: '#FEF3E2', c: '#B45309', label: '⚠ Conditional' },
  fail:        { bg: '#FCEBEB', c: '#A32D2D', label: '❌ Fail' },
};

// Within tolerance? Returns null when either side is missing — an ABSENT TARGET IS NOT A PASS,
// and rendering "✓" against a target nobody declared would be the system agreeing with itself.
const within = (measured, target, tol) => {
  const m = num(measured), t = num(target);
  if (m === null || t === null) return null;
  const band = num(tol);
  return band === null ? m === t : Math.abs(m - t) <= band;
};

export default function GreigeQC() {
  const { user } = useAuth();
  // A held lot (fail / conditional) is released for the dye-lot draw by a SECOND person with a reason
  // (ruled 25 Sep). The route allows admin/management and refuses the inspector who recorded it.
  const release = async (r) => {
    const reason = askReason(`Release ${r.lot_no} (GPO ${r.gpo_no} ${r.component}) for dyeing?\n\nIt was judged ${String(r.result).toUpperCase()}${r.remarks ? `: "${r.remarks}"` : ''} by ${r.checked_by_name || 'the inspector'}.\n\nOn what grounds is it going to the dyer anyway? (required — recorded against your name)`);
    if (reason == null) return;
    try { await api.post(`/jobwork/lot-members/${r.id}/qc/release`, { reason }); load(); }
    catch (e) { window.alert(e.response?.data?.error || 'Release failed'); }
  };
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);      // the member being judged
  const [detail, setDetail] = useState(null);  // GET .../qc — member, stage, target, qc
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [onlyPending, setOnlyPending] = useState(true);

  const load = useCallback(() => {
    api.get(`/jobwork/lot-members/qc-queue?stage=grey${onlyPending ? '&pending=1' : ''}`)
      .then(r => setRows(r.data.data || []))
      .catch(e => setErr(e.response?.data?.error || 'Could not load the greige queue'));
  }, [onlyPending]);
  useEffect(() => { load(); }, [load]);

  const openMember = async (m) => {
    setErr(''); setNotice(''); setOpen(m); setDetail(null);
    setForm({ result: '', remarks: '', gsm_measured: '', width_measured: '', dia_measured: '',
              loop_length_measured: '', shrinkage_warp_measured: '', shrinkage_weft_measured: '',
              four_point_points: '', shade_ok: '' });
    try {
      const r = await api.get(`/jobwork/lot-members/${m.id}/qc`);
      setDetail(r.data.data);
      // Seed dia from the declaration: the inspector confirms or corrects it rather than
      // re-keying a number that is already on the order.
      setForm(f => ({ ...f, dia_measured: r.data.data?.target?.machine_dia || '' }));
    } catch (e) { setErr(e.response?.data?.error || 'Could not load this lot'); }
  };

  const save = async () => {
    if (!form.result) { setErr('Pick a verdict.'); return; }
    if (form.result !== 'pass' && !String(form.remarks || '').trim()) {
      setErr('A reason is required for anything but a clean pass — someone else releases this fabric for dyeing on the strength of what you write.'); return;
    }
    setBusy(true); setErr('');
    try {
      const body = { result: form.result, remarks: form.remarks };
      for (const k of ['gsm_measured', 'width_measured', 'loop_length_measured',
                       'shrinkage_warp_measured', 'shrinkage_weft_measured', 'four_point_points'])
        if (form[k] !== '' && form[k] != null) body[k] = Number(form[k]);
      if (String(form.dia_measured || '').trim()) body.dia_measured = String(form.dia_measured).trim();
      if (form.shade_ok !== '') body.shade_ok = form.shade_ok === 'yes';
      const r = await api.post(`/jobwork/lot-members/${open.id}/qc`, body);
      setNotice(r.data.held_from_draw
        ? `Recorded as ${form.result.toUpperCase()}. This fabric is HELD from the dye-lot draw until a second person releases it.`
        : `Recorded as PASS. This fabric may be drawn into a dye lot.`);
      setOpen(null); setDetail(null); load();
    } catch (e) { setErr(e.response?.data?.error || 'Could not record the verdict'); }
    finally { setBusy(false); }
  };

  // One target/measured pair. The target is shown even when it is absent, because "not declared"
  // is a fact the inspector should see rather than a blank that reads as nothing to check.
  const Field = ({ label, k, target, tol, unit, text }) => {
    const ok = text ? null : within(form[k], target, tol);
    return (
      <div>
        <label style={labelStyle}>{label}</label>
        <div style={{ fontSize: 11, color: target == null || target === '' ? '#c0392b' : '#666', marginBottom: 3 }}>
          target: {target == null || target === '' ? 'not declared' : `${target}${tol != null && tol !== '' ? ` ± ${tol}` : ''}${unit || ''}`}
        </div>
        <input style={{ ...inputStyle, borderColor: ok === false ? '#E0B4B4' : '#ddd' }}
          type={text ? 'text' : 'number'} step="0.001"
          value={form[k] ?? ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
        {ok === false && <div style={{ fontSize: 10, color: '#B45309', marginTop: 2 }}>outside the declared band — your call</div>}
      </div>
    );
  };

  const t = detail?.target || {};

  return (
    <div>
      <div style={{ marginBottom: 10, padding: '8px 12px', background: '#EEEDF8', borderRadius: 6, fontSize: 12, color: '#3B3A8F' }}>
        🧵 Every greige lot is judged per component, against the fabric line declared for it. A pass lets its
        fabric be drawn into a dye lot; a fail or a conditional holds it until a second person releases it.
      </div>
      {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{err}</div>}
      {notice && <div style={{ background: '#EAF7EE', color: '#166534', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{notice}</div>}

      <label style={{ fontSize: 12, color: '#555', display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <input type="checkbox" checked={onlyPending} onChange={e => setOnlyPending(e.target.checked)} />
        show only lots still awaiting a verdict
      </label>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            {['Lot', 'Knitter lot', 'GPO', 'Component', 'kg back', 'Knitter', 'Verdict', ''].map(h =>
              <th key={h} style={{ padding: '8px', textAlign: 'left', color: '#555', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const v = r.result ? VERDICT[r.result] : null;
              // The recorder cannot sign — the same rule the endpoint enforces, shown here so the
              // button is not offered and then refused.
              const mine = String(r.recorded_by) === String(user?.id);
              // The lot's creator is barred too (8 Oct). Where creator and recorder are one login the
              // recorder line is the one shown, as on the route.
              const created = !mine && r.lot_created_by && String(r.lot_created_by) === String(user?.id);
              return (
                <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: 8, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{r.lot_no}</td>
                  <td style={{ padding: 8, color: '#666' }}>{r.vendor_lot_no || '—'}</td>
                  <td style={{ padding: 8 }}>{r.gpo_no}</td>
                  <td style={{ padding: 8 }}>{r.component}</td>
                  <td style={{ padding: 8, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(r.out_kg ?? 0).toLocaleString('en-IN')}</td>
                  <td style={{ padding: 8, color: '#666' }}>{r.vendor_name || '—'}</td>
                  <td style={{ padding: 8 }}>
                    {v ? <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: v.bg, color: v.c }}
                           title={r.remarks || ''}>{v.label}</span>
                       : <span style={{ color: '#B45309', fontWeight: 600 }}>awaiting</span>}
                    {r.result && r.result !== 'pass' && (r.draw_released_by
                      ? <span style={{ marginLeft: 6, fontSize: 10, color: '#166534' }}>released</span>
                      : <span style={{ marginLeft: 6, fontSize: 10, color: '#A32D2D' }}>held from draw</span>)}
                  </td>
                  <td style={{ padding: 8 }}>
                    {/* THE RELEASE HAD NO BUTTON. This screen promised "a second person releases it",
                        POST /lot-members/:id/qc/release existed (admin/management), and no screen called
                        it — so a fail or conditional could never be dyed. Found by the ruling 36 check,
                        2 Oct 2026. Offered only where the route would accept it, and said otherwise. */}
                    {r.result && r.result !== 'pass' && !r.draw_released_by && (
                      !RELEASERS.includes(user?.role)
                        ? <span style={{ fontSize: 10, color: '#888' }}>admin or management releases this</span>
                        : String(r.checked_by) === String(user?.id)
                          ? <span style={{ fontSize: 10, color: '#A32D2D' }} title="The route refuses the inspector releasing their own verdict.">you judged it — someone else releases</span>
                          : <button onClick={() => release(r)}
                              style={{ padding: '5px 12px', background: '#166534', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>↗ Release for dyeing</button>)}
                    {!r.result && (mine
                      ? <span style={{ fontSize: 10, color: '#A32D2D' }} title="You keyed the receipt this lot came from.">you recorded this receipt</span>
                      : created
                      ? <span style={{ fontSize: 10, color: '#A32D2D' }} title="You resolved the weight hold that created this lot.">you created this lot</span>
                      : <button onClick={() => openMember(r)}
                          style={{ padding: '5px 12px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>🔬 Judge</button>)}
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={8} style={{ padding: 16, textAlign: 'center', color: '#aaa' }}>
              {onlyPending ? 'No greige lots are waiting for a verdict.' : 'No greige lots yet.'}</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 720, maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#3B3A8F', marginBottom: 2 }}>
              Greige QC — {open.lot_no} · {open.component}
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 14 }}>
              GPO {open.gpo_no} · {open.vendor_name || '—'} · {Number(open.out_kg ?? 0).toLocaleString('en-IN')} kg back
              {open.vendor_lot_no ? ` · knitter's lot ${open.vendor_lot_no}` : ''}
              {detail?.target?.machine_label ? ` · ${detail.target.machine_label}` : ''}
            </div>
            {!detail && <div style={{ color: '#888', fontSize: 12 }}>loading the declared target…</div>}
            {detail && (<>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
                {/* GREIGE IS WHAT IS JUDGED (ruling 23). The finished figure sits beside it as
                    context, never as the band. Where Fabrics entered no greige target the field
                    says "not declared" and offers nothing to judge against — falling back to the
                    finished target would guarantee a false fail, because greige is heavier. */}
                <Field label="GSM (greige)" k="gsm_measured"
                  target={detail?.greige?.gsm_target} tol={detail?.greige?.gsm_tolerance} />
                <div>
                  <label style={labelStyle}>GSM (finished)</label>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>context only — not what this is judged against</div>
                  <div style={{ padding: '7px 9px', border: '1px dashed #ddd', borderRadius: 6, fontSize: 12, color: '#555', background: '#fafafa' }}>
                    {t.gsm_target == null ? 'not declared'
                      : `${t.gsm_target}${t.gsm_tolerance != null ? ` ± ${t.gsm_tolerance}` : ''}`}
                  </div>
                </div>
                <Field label="Width" k="width_measured" target={t.width_target} unit='"' />
                <Field label="Dia" k="dia_measured" target={t.machine_dia} text />
                <Field label="Shrinkage warp %" k="shrinkage_warp_measured" target={t.shrinkage_warp} />
                <Field label="Shrinkage weft %" k="shrinkage_weft_measured" target={t.shrinkage_weft} />
                <Field label="Loop length" k="loop_length_measured" target={null} />
                <div>
                  <label style={labelStyle}>4-point total</label>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>points on this lot</div>
                  <input style={inputStyle} type="number" step="0.01" value={form.four_point_points}
                    onChange={e => setForm(f => ({ ...f, four_point_points: e.target.value }))} />
                </div>
                <div>
                  <label style={labelStyle}>Shade vs swatch</label>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>&nbsp;</div>
                  <select style={inputStyle} value={form.shade_ok} onChange={e => setForm(f => ({ ...f, shade_ok: e.target.value }))}>
                    <option value="">— not checked —</option>
                    <option value="yes">Matches</option>
                    <option value="no">Does not match</option>
                  </select>
                </div>
              </div>

              {!detail?.greige && (
                <div style={{ marginBottom: 12, background: '#FEF3E2', border: '1px solid #f0d9ae', color: '#7a4b00', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
                  ⓘ No greige GSM was set on this knitting order, so there is nothing to judge the GSM
                  against. Record what you measure; the finished figure beside it is <strong>not</strong> the
                  greige target — greige is heavier, and judging against it would fail good cloth.
                </div>
              )}
              <div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
                <label style={labelStyle}>Your verdict *</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {['pass', 'conditional', 'fail'].map(k => (
                    <button key={k} type="button" onClick={() => setForm(f => ({ ...f, result: k }))}
                      style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        border: form.result === k ? `2px solid ${VERDICT[k].c}` : '1px solid #ddd',
                        background: form.result === k ? VERDICT[k].bg : 'white', color: form.result === k ? VERDICT[k].c : '#555' }}>
                      {VERDICT[k].label}
                    </button>
                  ))}
                </div>
                <label style={labelStyle}>
                  Reason {form.result && form.result !== 'pass' ? '* (required)' : '(optional on a clean pass)'}
                </label>
                <textarea rows={2} value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                  placeholder="What you saw, in your words. A second person releases this fabric on the strength of it."
                  style={{ ...inputStyle, borderColor: form.result && form.result !== 'pass' && !String(form.remarks || '').trim() ? '#E0B4B4' : '#ddd' }} />
                {form.result && form.result !== 'pass' && (
                  <div style={{ marginTop: 8, background: '#FEF3E2', border: '1px solid #f0d9ae', color: '#7a4b00', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
                    🛡 This holds the fabric from the dye-lot draw. It is not a block — a second person can
                    release it with a recorded reason. Record what you actually saw.
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
                <button onClick={() => { setOpen(null); setDetail(null); }}
                  style={{ padding: '8px 16px', background: '#f0f2f8', color: '#444', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                <button disabled={busy} onClick={save}
                  style={{ padding: '8px 18px', background: '#166534', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>
                  {busy ? 'Recording…' : 'Record verdict'}</button>
              </div>
            </>)}
          </div>
        </div>
      )}
    </div>
  );
}
